// Sales ▸ Consumer Voice Reports: the full internal history — ad-hoc runs
// of the public /consumer-voice-audit tool (anonymous or signed-in), catalog
// product runs (product page / brand portal / RMN) and brand runs — with
// view / PDF / Create strategy per row. The Consumer Voice sibling of the
// PDP Audit Reports tab, including its type filter (?kind=product|brand).
/* eslint-disable react/no-unused-prop-types -- the list props are spread into useResourceList, as in SalesAdmin's tabs */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { FileDownloadOutlined, VisibilityOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import { salesConsumerVoiceReportEndpoint, salesConsumerVoiceReportsEndpoint } from "../../api/endpoints";
import { RequestedByCell, RequestedByUser } from "../../components/RequestedByCell";
import { DataTable, DataTableColumn } from "../../components/DataTable/DataTable";
import { CvScoreChip } from "../../components/ConsumerVoice/CvScoreChip";
import { ConsumerVoiceReportDialog } from "../../components/ConsumerVoice/ConsumerVoiceReportDialog";
import { downloadConsumerVoicePdf } from "../../components/ConsumerVoice/consumerVoiceFiles";
import { ConsumerVoiceAudit, cvSubjectLabel, isCvRunning } from "../../types/consumerVoice";
import { useResourceList } from "../../hooks/useResourceList";

const PAGE_SIZE = 25;
const icon = "w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed";

type SalesCvAudit = ConsumerVoiceAudit & {
    lead?: { id: number; email: string } | null;
    user?: RequestedByUser | null;
    // Catalog runs: the joined product / subject brand (labels fall back to these).
    product?: { desireid: number; title: string } | null;
    subject_brand?: { id: number; name: string } | null;
};

// Brand run, catalog product run (desireid), or ad-hoc URL run.
const kindLabel = (r: SalesCvAudit) => {
    if (r.kind === "brand") return "Brand";
    return r.desireid ? "Catalog" : "URL";
};

const KIND_OPTIONS: Array<{ value: string; label: string }> = [
    { value: "", label: "All" },
    { value: "product", label: "Product" },
    { value: "brand", label: "Brand" },
];

interface Props {
    searchParams: URLSearchParams;
    setSearchParams: (updater: any, opts?: any) => void;
    page: number;
    setPage: (p: number) => void;
    searchText: string;
    extraParams?: Record<string, string | undefined>;
    onCountChange?: (count: number) => void;
}

export function ConsumerVoiceReportsTab({ extraParams, ...props }: Props) {
    const navigate = useNavigate();
    const [viewingId, setViewingId] = useState<number | null>(null);

    // Deep-linked kind filter, as the PDP audit tab's report_type; changing
    // a non-ignored search param resets pagination and refetches.
    const kindFilter = props.searchParams.get("kind") || "";
    const setKindFilter = (value: string) => {
        props.setSearchParams((prev: URLSearchParams) => {
            if (value) prev.set("kind", value);
            else prev.delete("kind");
            return prev;
        });
    };

    const list = useResourceList<SalesCvAudit>({
        endpoint: salesConsumerVoiceReportsEndpoint,
        pageSize: PAGE_SIZE,
        errorLabel: "Failed to load Consumer Voice reports",
        ...props,
        extraParams: { ...extraParams, kind: kindFilter || undefined },
    });

    // Same hand-off the product settings tab uses: the wizard reads ?cv=<id>
    // and derives the objective from the report.
    const createStrategy = (audit: ConsumerVoiceAudit) => {
        const params = new URLSearchParams({ cv: String(audit.id) });
        if (audit.url) params.set("url", audit.url);
        navigate(`/proposal?${params.toString()}`);
    };

    const columns: DataTableColumn<SalesCvAudit>[] = [
        {
            key: "email",
            header: "Email",
            render: (r) => <RequestedByCell user={r.user} lead={r.lead} />,
        },
        {
            key: "kind",
            header: "Type",
            render: (r) => (
                <span className="text-[11px] bg-surface-2 text-text-2 px-2 py-[2px] rounded-md font-semibold">
                    {kindLabel(r)}
                </span>
            ),
        },
        {
            key: "subject",
            header: "Subject",
            render: (r) => (
                <div className="min-w-0">
                    <div className="font-semibold text-text-1 truncate max-w-[320px]" title={cvSubjectLabel(r)}>
                        {cvSubjectLabel(r)}
                    </div>
                    {r.kind !== "brand" && r.params.brand_name && (
                        <div className="text-[11px] text-text-2 truncate max-w-[320px]">{r.params.brand_name}</div>
                    )}
                    {r.url && (
                        <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-[11px] text-muted truncate max-w-[320px] hover:text-accent-text"
                            title={r.url}
                        >
                            {r.url}
                        </a>
                    )}
                </div>
            ),
        },
        {
            key: "status",
            header: "Status",
            render: (r) => {
                if (r.status === "ready") {
                    return (
                        <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#2E7D32] bg-[#E8F5E9] rounded-full px-2.5 py-1">
                            Ready
                        </span>
                    );
                }
                if (isCvRunning(r)) {
                    return (
                        <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-accent-text bg-tint rounded-full px-2.5 py-1">
                            Running…
                        </span>
                    );
                }
                return (
                    <Tooltip title={r.generation_error || "Failed"}>
                        <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#c62828] bg-[#FBEFEF] rounded-full px-2.5 py-1">
                            Failed
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            key: "score",
            header: "CV Score",
            render: (r) =>
                r.status === "ready" ? <CvScoreChip score={r.overall_score} subscores={r.subscores} /> : <span className="text-muted">—</span>,
        },
        {
            key: "created",
            header: "Created",
            render: (r) => <span className="text-[12px] text-text-2 whitespace-nowrap">{dayjs(r.created_at).format("MMM DD, YYYY h:mm A")}</span>,
        },
        {
            key: "actions",
            header: "",
            render: (r) => (
                <div className="flex items-center justify-end gap-1">
                    <Tooltip title="View analysis">
                        <span>
                            <button
                                type="button"
                                aria-label="View analysis"
                                disabled={r.status !== "ready"}
                                onClick={() => setViewingId(r.id)}
                                className={icon}
                            >
                                <VisibilityOutlined sx={{ fontSize: 15 }} />
                            </button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Download PDF">
                        <span>
                            <button
                                type="button"
                                aria-label="Download analysis"
                                disabled={r.status !== "ready"}
                                onClick={() => downloadConsumerVoicePdf(salesConsumerVoiceReportEndpoint(r.id))}
                                className={icon}
                            >
                                <FileDownloadOutlined sx={{ fontSize: 15 }} />
                            </button>
                        </span>
                    </Tooltip>
                    <button
                        type="button"
                        title={r.kind === "brand" ? "Strategies start from a product analysis" : "Start a strategy from this analysis"}
                        disabled={r.status !== "ready" || r.kind === "brand"}
                        onClick={() => createStrategy(r)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-border-token rounded-md bg-surface text-[11.5px] font-medium text-accent-text cursor-pointer hover:border-accent hover:bg-tint disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Create strategy
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="flex items-center gap-1.5 mb-4">
                {KIND_OPTIONS.map((opt) => {
                    const active = kindFilter === opt.value;
                    return (
                        <button
                            key={opt.value || "all"}
                            type="button"
                            onClick={() => setKindFilter(opt.value)}
                            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold cursor-pointer border-0 transition-colors ${
                                active ? "bg-tint text-accent-text" : "bg-surface-2 text-text-2 hover:text-text-1"
                            }`}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
            <DataTable<SalesCvAudit>
                columns={columns}
                rows={list.items}
                loading={list.loading}
                totalCount={list.totalCount}
                hasMore={list.hasMore}
                error={list.error}
                onLoadMore={list.loadMore}
                emptyMessage="No Consumer Voice reports yet — run one with New CV Analysis."
                getRowKey={(r) => r.id}
            />
            {viewingId !== null && (
                <ConsumerVoiceReportDialog
                    endpoint={salesConsumerVoiceReportEndpoint(viewingId)}
                    productEndpoint={salesConsumerVoiceReportEndpoint}
                    onClose={() => setViewingId(null)}
                    onCreateStrategy={createStrategy}
                    createStrategyLabel="Create strategy"
                />
            )}
        </>
    );
}
